import { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Home from "./pages/Home";
import Status from "./pages/Status";
import Incidents from "./pages/Incidents";
import Tracker from "./pages/Tracker";
import Careers from "./pages/Careers";
import CareersView from "./pages/CareersView";
import CareersCreate from "./pages/CareersCreate";
import CareersReview from "./pages/CareersReview";
import CareersDepth from "./pages/CareersDepth";
import Login from "./pages/Login";
import MyApplication from "./pages/MyApplication";
import BanAppeal from "./pages/BanAppeal";
import AppealsReview from "./pages/AppealsReview";
import AppealsDepth from "./pages/AppealsDepth";
import StaffAgreement from "./pages/StaffAgreement";
import StaffTeam from "./pages/Staff-Team";
import StaffAgreementRoute from "./components/StaffAgreementRoute";
import TermsOfService from "./pages/TermsOfService";
import Admin from "./pages/Admin";
import Manage from "./pages/Manage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import Transcript from "./pages/Transcript";
import NotFound from "./pages/NotFound";
import Footer from "./components/Footer";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import News from "./pages/News";
import NewsViewer from "./pages/NewsViewer";
import { supabase } from "./supabaseClient";

const DEFAULT_TABS = {
  page_news: true,
  page_tracker: true,
  page_careers: true,
  page_status: true,
  page_staff_team: true,
  page_ban_appeal: true,
  page_my_application: true,
  page_terms: true,
  page_privacy: true,
  page_cookies: true,
};

function PageGate({ enabled, children }) {
  if (!enabled) return <NotFound />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const [tabs, setTabs] = useState(DEFAULT_TABS);

  const isTabEnabled = useMemo(() => {
    return (key) => tabs[key] !== false;
  }, [tabs]);

  useEffect(() => {
    async function loadTabs() {
      try {
        const { data, error } = await supabase
          .from("Tabs")
          .select("tab_key, enabled");

        if (error) throw error;

        const nextTabs = { ...DEFAULT_TABS };

        if (Array.isArray(data)) {
          data.forEach((tab) => {
            if (tab?.tab_key) {
              nextTabs[tab.tab_key] = tab.enabled === true;
            }
          });
        }

        setTabs(nextTabs);
      } catch (err) {
        console.error("[App] Failed to load page tabs:", err);
        setTabs(DEFAULT_TABS);
      }
    }

    loadTabs();
  }, []);

  const isStatusSubdomain =
    typeof window !== "undefined" &&
    ["status.outfall.co.uk", "status.outfall.website"].includes(
      window.location.hostname,
    );

  const hideGlobalFooter =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/manage") ||
    location.pathname.startsWith("/status") ||
    location.pathname.startsWith("/news-builder") ||
    location.pathname.startsWith("/careers-review") ||
    location.pathname.startsWith("/careers-create") ||
    location.pathname.startsWith("/incidents");

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            isStatusSubdomain ? (
              <PageGate enabled={isTabEnabled("page_status")}>
                <Status />
              </PageGate>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />

        <Route path="/home" element={<Home />} />

        <Route
          path="/our-team"
          element={
            <PageGate enabled={isTabEnabled("page_staff_team")}>
              <StaffTeam />
            </PageGate>
          }
        />

        <Route
          path="/tracker"
          element={
            <PageGate enabled={isTabEnabled("page_tracker")}>
              <Tracker />
            </PageGate>
          }
        />

        <Route
          path="/status"
          element={
            <PageGate enabled={isTabEnabled("page_status")}>
              <Status />
            </PageGate>
          }
        />

        <Route
          path="/status/incidents/:id"
          element={
            <PageGate enabled={isTabEnabled("page_status")}>
              <Incidents />
            </PageGate>
          }
        />

        <Route
          path="/incidents/:id"
          element={
            <PageGate enabled={isTabEnabled("page_status")}>
              <Incidents />
            </PageGate>
          }
        />

        <Route
          path="/news"
          element={
            <PageGate enabled={isTabEnabled("page_news")}>
              <News />
            </PageGate>
          }
        />

        <Route
          path="/news/:slug"
          element={
            <PageGate enabled={isTabEnabled("page_news")}>
              <NewsViewer />
            </PageGate>
          }
        />

        <Route
          path="/careers"
          element={
            <PageGate enabled={isTabEnabled("page_careers")}>
              <Careers />
            </PageGate>
          }
        />

        <Route
          path="/careers/:slug"
          element={
            <PageGate enabled={isTabEnabled("page_careers")}>
              <CareersView />
            </PageGate>
          }
        />

        <Route path="/careers-create" element={<CareersCreate />} />
        <Route path="/login" element={<Login />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/manage"
          element={
            <ProtectedRoute>
              <Manage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-application"
          element={
            <PageGate enabled={isTabEnabled("page_my_application")}>
              <MyApplication />
            </PageGate>
          }
        />

        <Route
          path="/ban-appeal"
          element={
            <PageGate enabled={isTabEnabled("page_ban_appeal")}>
              <BanAppeal />
            </PageGate>
          }
        />

        <Route
          path="/appeals-review"
          element={
            <ProtectedRoute>
              <AppealsReview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/appeals-review/:id"
          element={
            <ProtectedRoute>
              <AppealsDepth />
            </ProtectedRoute>
          }
        />

        <Route
          path="/careers-review"
          element={
            <ProtectedRoute>
              <CareersReview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/careers-review/:id"
          element={
            <ProtectedRoute>
              <CareersDepth />
            </ProtectedRoute>
          }
        />

        <Route
          path="/terms-of-services"
          element={
            <PageGate enabled={isTabEnabled("page_terms")}>
              <TermsOfService />
            </PageGate>
          }
        />

        <Route
          path="/privacy-policy"
          element={
            <PageGate enabled={isTabEnabled("page_privacy")}>
              <PrivacyPolicy />
            </PageGate>
          }
        />

        <Route
          path="/cookie-policy"
          element={
            <PageGate enabled={isTabEnabled("page_cookies")}>
              <CookiePolicy />
            </PageGate>
          }
        />

        <Route
          path="/staff-agreement"
          element={
            <StaffAgreementRoute>
              <StaffAgreement />
            </StaffAgreementRoute>
          }
        />

        <Route path="/transcript/:slug" element={<Transcript />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {!hideGlobalFooter && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
